import { get, set, del, keys } from 'idb-keyval';

/** Store portal token in IDB so the service worker can read it */
export async function storeTokenInIDB(token: string): Promise<void> {
  await set('portal-token', token);
}

/** Request background sync for queued uploads */
export async function requestBackgroundSync(): Promise<void> {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const reg = await navigator.serviceWorker.ready;
    try {
      await (reg as any).sync.register('sync-uploads');
    } catch {
      // Background Sync not supported, will sync on next app open
    }
  }
}

export interface QueuedUpload {
  id: string;
  file: ArrayBuffer;
  fileName: string;
  mimeType: string;
  queuedAt: string;
  synced: boolean;
}

const QUEUE_PREFIX = 'upload-queue:';

export async function queueUpload(file: File): Promise<string> {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const buffer = await file.arrayBuffer();

  const entry: QueuedUpload = {
    id,
    file: buffer,
    fileName: file.name,
    mimeType: file.type,
    queuedAt: new Date().toISOString(),
    synced: false,
  };

  await set(`${QUEUE_PREFIX}${id}`, entry);
  return id;
}

export async function getPendingUploads(): Promise<QueuedUpload[]> {
  const allKeys = await keys();
  const queueKeys = allKeys.filter((k) => String(k).startsWith(QUEUE_PREFIX));
  const entries: QueuedUpload[] = [];

  for (const key of queueKeys) {
    const entry = await get<QueuedUpload>(key);
    if (!entry) continue;
    // Clean up stale entries marked synced by the service worker
    if (entry.synced) {
      await del(key);
      continue;
    }
    entries.push(entry);
  }

  return entries.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function markSynced(id: string): Promise<void> {
  await del(`${QUEUE_PREFIX}${id}`);
}

export async function removeUpload(id: string): Promise<void> {
  await del(`${QUEUE_PREFIX}${id}`);
}

export async function syncPendingUploads(): Promise<{ synced: number; failed: number }> {
  // Clean up any stale synced entries left by the service worker
  const allKeys = await keys();
  for (const key of allKeys) {
    const k = String(key);
    if (k.startsWith(QUEUE_PREFIX)) {
      const entry = await get<QueuedUpload>(key);
      if (entry?.synced) await del(key);
    }
  }

  const pending = await getPendingUploads();
  let synced = 0;
  let failed = 0;

  for (const entry of pending) {
    try {
      const file = new File([entry.file], entry.fileName, { type: entry.mimeType });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('queuedAt', entry.queuedAt);

      const token = localStorage.getItem('portal-token');
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/portal/upload`, {
        method: 'POST',
        headers: token ? { 'X-Portal-Token': token } : {},
        body: formData,
      });

      if (res.ok) {
        await markSynced(entry.id);
        synced++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  return { synced, failed };
}
